import React from "react";
import {useCurrentFrame, useVideoConfig, spring, interpolate} from "remotion";
import {SPRINGS} from "../../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../../presets/idv";

/**
 * KineticText — 6 animation modes for text that feels human-edited.
 * Replaces static div+opacity pattern with per-word choreography.
 *
 * Modes:
 *  - stagger-up: words slide up one by one (default, clean)
 *  - stagger-scale: words scale in from 0.7 (punchy)
 *  - stagger-blur: words deblur + fade (cinematic)
 *  - wave: words animate in a wave pattern (playful)
 *  - split-reveal: text splits from center outward (dramatic)
 *  - bounce: words bounce in with overshoot (energetic)
 */

export type KineticMode = "stagger-up" | "stagger-scale" | "stagger-blur" | "wave" | "split-reveal" | "bounce";

export interface KineticTextProps {
  text: string;
  mode?: KineticMode;
  delay?: number;
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  highlightWords?: string[];
  highlightColor?: string;
  gap?: number;
  lineHeight?: number;
  maxWidth?: string;
  textAlign?: "left" | "center" | "right";
  fontFamily?: string;
}

export const KineticText: React.FC<KineticTextProps> = ({
  text,
  mode = "stagger-up",
  delay = 0,
  fontSize = 48,
  fontWeight = 700,
  color = "#f5f5f7",
  highlightWords = [],
  highlightColor = "#00DF82",
  gap = 4,
  lineHeight = 1.3,
  maxWidth = "90%",
  textAlign = "center",
  fontFamily = FONT_STACK,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const words = text.split(" ");
  const center = Math.floor(words.length / 2);

  const highlightSet = new Set(highlightWords.map(w => w.toLowerCase()));

  const getWordStyle = (wordIdx: number): React.CSSProperties => {
    let wordDelay: number;

    switch (mode) {
      case "split-reveal":
        wordDelay = delay + Math.abs(wordIdx - center) * gap;
        break;
      case "wave":
        wordDelay = delay + wordIdx * gap;
        break;
      default:
        wordDelay = delay + wordIdx * gap;
    }

    const p = spring({
      frame: frame - wordDelay,
      fps,
      config: mode === "bounce"
        ? {damping: 8, stiffness: 180, mass: 0.6}
        : mode === "stagger-scale"
          ? SPRINGS.snappy
          : {damping: 14, stiffness: 120, mass: 0.8},
    });

    const base: React.CSSProperties = {
      display: "inline-block",
      marginRight: 8,
      fontSize: Math.max(fontSize, 32),
      fontWeight,
      fontFamily,
      lineHeight,
    };

    switch (mode) {
      case "stagger-up":
        return {
          ...base,
          opacity: p,
          transform: `translateY(${interpolate(p, [0, 1], [20, 0], CLAMP)}px)`,
        };
      case "stagger-scale":
        return {
          ...base,
          opacity: p,
          transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
        };
      case "stagger-blur":
        return {
          ...base,
          opacity: p,
          filter: `blur(${interpolate(p, [0, 1], [8, 0], CLAMP)}px)`,
        };
      case "wave": {
        const waveY = Math.sin((frame - wordDelay) * 0.15) * 3 * (1 - p);
        return {
          ...base,
          opacity: p,
          transform: `translateY(${interpolate(p, [0, 1], [25, 0], CLAMP) + waveY}px)`,
        };
      }
      case "split-reveal":
        return {
          ...base,
          opacity: p,
          transform: `translateX(${interpolate(p, [0, 1], [wordIdx < center ? -30 : 30, 0], CLAMP)}px)`,
        };
      case "bounce":
        return {
          ...base,
          opacity: p,
          transform: `translateY(${interpolate(p, [0, 1], [40, 0], CLAMP)}px) scale(${interpolate(p, [0, 1], [0.5, 1], CLAMP)})`,
        };
      default:
        return {...base, opacity: p};
    }
  };

  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: textAlign === "center" ? "center" : textAlign === "right" ? "flex-end" : "flex-start",
      maxWidth,
      gap: 0,
    }}>
      {words.map((word, i) => {
        const isHighlight = highlightSet.has(word.toLowerCase().replace(/[.,!?;:]/g, ""));
        return (
          <span key={i} style={{
            ...getWordStyle(i),
            color: isHighlight ? highlightColor : color,
          }}>
            {word}
          </span>
        );
      })}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
